import { ThemeProvider } from '@emotion/react';
import { useEffect, useState, useReducer } from 'react';
import axios from 'axios';
import { Heading, LabelText, ToolLoader } from 'shared/components';
import defaultTheme from 'shared/defaultTheme';
import { getToolSettings, reducer } from '../helpers';
import { OverviewContainer, BrokenLayout } from './Layout';
import { initialState, UNPROCESSABLE_ENTITY } from '../constants';
import { Input, SelectInput } from '../../../shared/inputs';
import { get } from 'lodash';
import { PrimaryButton } from 'shared/buttons';

const App = () => {
  // Config
  const [theme, setTheme] = useState(defaultTheme);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [broken, setBroken] = useState(false);
  const [errors, setErrors] = useState({});
  const [formIsSent, setFormIsSent] = useState(false);
  // Data
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    postal_code,
    house_number,
    house_number_addition,
    motivation_for_estimation,
    plans_to_move,
    first_name,
    last_name,
    last_name_prefix,
    email,
    phone_number,
  } = state;

  // Configure tool
  useEffect(() => {
    async function configure() {
      const request = await getToolSettings(setBroken);
      const { settings, meta } = request;
      setTheme(theme => ({ ...theme, ...settings }));
      setMeta(meta);
      setLoading(false);
    }

    configure();
  }, []);

  if (loading) return <ToolLoader />;
  if (broken) return <BrokenLayout />;

  const onChange = e => {
    dispatch({ key: e.name || e.target.name, value: e.value || e.target.value });
  };

  const onSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setErrors({})

    const prepareState = {...state, postal_code: state.postal_code.toUpperCase()}

    try {
      await axios({
        method: 'post',
        url: '/woningwaarde/lead',
        data: prepareState,
      });
      setFormIsSent(true);
    } catch ({ response: { status, data } }) {
      if (status === UNPROCESSABLE_ENTITY) setErrors(data.errors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <OverviewContainer>
        {formIsSent ? (
          <div css={{ maxWidth: '600px' }}>
            <Heading>Je aanvraag is verstuurd!</Heading>
            <p>Wij zullen er zo snel mogelijk voor zorgen dat je jouw gratis woningwaarde rapport op het e-mailadres wat je hebt ingevoerd ontvangt.</p>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <Heading css={{ marginBottom: 8 }}>Vul je gegevens in en ontvang het gratis woningwaarde rapport</Heading>
            <h2>Voor welke woning wil je een rapport?</h2>

            <LabelText>Postcode*</LabelText>
            <Input
              name="postal_code"
              value={postal_code}
              onChange={({ target: { name, value }}) => {
                const valueWithoutSpaces = value.replace(/\s/g, '');

                onChange({ name, value: valueWithoutSpaces });
              }}
              errors={get(errors, 'postal_code', null)}
              placeholder="1234AB"
            />
            <LabelText>Huisnummer*</LabelText>
            <Input
              name="house_number"
              value={house_number}
              onChange={onChange}
              errors={get(errors, 'house_number', null)}
              placeholder="123"
              type="number"
            />
            <LabelText>Toevoeging</LabelText>
            <Input
              name="house_number_addition"
              value={house_number_addition}
              onChange={onChange}
              errors={get(errors, 'house_number_addition', null)}
              placeholder="AB"
            />
            <LabelText>Waarom wil je een waardeschatting?*</LabelText>
            <SelectInput
              options={get(meta, 'motivationOptions', [])}
              value={motivation_for_estimation}
              name={'motivation_for_estimation'}
              onChange={({ value }) => onChange({ name: 'motivation_for_estimation', value })}
              placeholder="Maak een keuze"
              errors={get(errors, 'motivation_for_estimation', null)}
            />
            <LabelText>Wil je verhuizen?*</LabelText>
            <SelectInput
              options={get(meta, 'movingOptions', [])}
              value={plans_to_move}
              name={'plans_to_move'}
              onChange={({ value }) => onChange({ name: 'plans_to_move', value })}
              placeholder="Maak een keuze"
              errors={get(errors, 'plans_to_move', null)}
            />

            <h2 css={{ marginTop: '24px' }}>Contactgegevens</h2>
            <LabelText>Voornaam*</LabelText>
            <Input
              name="first_name"
              value={first_name}
              onChange={onChange}
              errors={get(errors, 'first_name', null)}
              placeholder="Jan"
            />
            <LabelText>tussenvoegsel</LabelText>
            <Input
              name="last_name_prefix"
              value={last_name_prefix}
              onChange={onChange}
              errors={get(errors, 'last_name_prefix', null)}
              placeholder="van"
            />
            <LabelText>Achternaam*</LabelText>
            <Input
              name="last_name"
              value={last_name}
              onChange={onChange}
              errors={get(errors, 'last_name', null)}
              placeholder="Janssen"
            />
            <LabelText>E-mailadres*</LabelText>
            <Input
              name="email"
              value={email}
              onChange={onChange}
              errors={get(errors, 'email', null)}
              placeholder="voorbeeld@gmail.com"
            />
            <LabelText>Telefoonnummer</LabelText>
            <Input
              name="phone_number"
              value={phone_number}
              onChange={onChange}
              errors={get(errors, 'phone_number', null)}
              placeholder="0612345678"
              type="number"
            />
            <PrimaryButton type="submit" css={{ marginTop: '16px' }}>Rapport aanvragen</PrimaryButton>
          </form>
        )}
      </OverviewContainer>
    </ThemeProvider>
  );
};

export default App;
